import { type MouseEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { flushSync } from 'react-dom';
import {
  type ColumnDef,
  type ColumnOrderState,
  type Row,
  type Updater,
  type VisibilityState,
  getCoreRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FileX, FolderInput, ImagePlus, Menu, UploadIcon, X } from 'lucide-react';
import { type UniqueIdentifier } from '@dnd-kit/core';
import { useShallow } from 'zustand/react/shallow';
import {
  AddAssetsToUploadMenuItem,
  ClearUploadMenuItem,
  ColumnsVisibilitySubmenu,
  ToggleViewMenu,
  ZoomInMenuItem,
  ZoomOutMenuItem,
} from '@/components/menu-items/node-menu-items';
import { type UploadSession, createEmptyUploadSession, useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTriggerButton } from '@/components/ui/dropdown-menu';
import { MenuProvider, MenuSeparator, MenuSub, dropdownPrimitives } from '@/components/menu-renderer';
import { type ColumnConfig, type SelectionEventProps } from '@/components/layouts/gallery-layout';
import { UploadsTreeSelectorDialog } from '@/components/inspector/uploads-tree-selector-dialog';
import { DataTableGridListView } from '@/components/data-table/data-table-grid-list-view';
import { TaskState, type UploadAsset, UploadFinishedPercentage } from '@/types/uploads';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { SelectAllCheckbox } from '@/components/toolbars/select-all-checkbox';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { buildColumns } from '@/components/data-table/build-columns';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { SelectionBar } from '@/components/toolbars/selection-bar';
import { useOverflowCollapse } from '@/hooks/useOverflowCollapse';
import { type BackendError } from '@/hooks/useFormErrorHandler';
import { DropdownSelect } from '@/components/dropdown-select';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { defaultColumnsSettings } from '@/types/datatable';
import { EmptyLayout } from '@/components/empty-layout';
import { Toolbar } from '@/components/toolbars/toolbar';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { useTreeContext } from '@/context/TreeContext';
import { Separator } from '@/components/ui/separator';
import { type Asset, Variants } from '@/types/asset';
import { DAMNodeType, NodeType } from '@/types/tree';
import { CsvDialog } from '@/components/csv-dialog';
import { Button } from '@/components/ui/button';
import { cn, transformPath } from '@/lib/utils';
import { ColumnType } from '@/types/column';
import { EntityType } from '@/types/entity';
import { GalleryView } from '@/types';
import { useTranslations } from 'next-intl';

const TABLE_ENTITY = EntityType.Ingestion;
// Placeholder rendered for the one frame before the effect below creates the first session on
// a cold load; `uploadFolderPath` stays empty so the layout shows its normal "no folder" state.
const EMPTY_SESSION: UploadSession = createEmptyUploadSession(false);

export const IngestionLayout = ({
  onError,
  keepExistingFoldersOnClear,
  hideBreadcrumb,
  disableFolderPicker,
  disableGrouping,
  emptyStateCopy,
  usePageInspector,
}: {
  isLoading?: boolean;
  onError?: BackendError | Error | null;
  /** Off for a contributor session, so clearing never re-enables folder recreation. */
  keepExistingFoldersOnClear?: boolean;
  /** On for a contributor session, which only ever has the one fixed folder. */
  hideBreadcrumb?: boolean;
  /** On for a contributor session: the empty-state button picks files, not whole OS folders. */
  disableFolderPicker?: boolean;
  /** On for a contributor session: everything is flattened into one folder, so no per-folder group header. */
  disableGrouping?: boolean;
  /** Overrides the empty-drop-state copy; falls back to the tenant uploader's copy. */
  emptyStateCopy?: { title: string; description: ReactNode; buttonLabel: string };
  /** On for a contributor session, whose host layout has no separate upload-inspector state. */
  usePageInspector?: boolean;
}) => {
  const { ref: toolbarRef, collapsed } = useOverflowCollapse<HTMLDivElement>();
  const tCommon = useTranslations('common');
  const tUpload = useTranslations('upload');

  const [grouping, _setGrouping] = useState<Array<string>>(disableGrouping ? [] : ['path']);
  const [isFolderPickerActive, setIsFolderPickerActive] = useState(false);

  const openModal = useOpenModal();

  const removeFolder = useUploadStore((state) => state.removeFolder);
  const onDrop = useUploadStore((state) => state.onDrop);
  const setUploadField = useUploadStore((state) => state.setUploadField);
  const getUploadSession = useUploadStore((state) => state.getUploadSession);
  const activeEntry = useUploadStore(useShallow((state) => Object.entries(state.uploads).find(([, s]) => s.active)));
  const createUploadSession = useUploadStore((state) => state.createUploadSession);
  useEffect(() => {
    if (!activeEntry) {
      createUploadSession();
    }
  }, [activeEntry, createUploadSession]);
  const [uploadSessionId, session] = activeEntry ?? ['', EMPTY_SESSION];
  const { contextNodeSelection } = useTreeContext();
  const thumbnailSize = useUserSettingsStore((s) => s.thumbnailSize);
  const galleryView = useUserSettingsStore((s) => s.galleryView);
  const {
    selectedAssets,
    filesByFolder,
    uploadFolderPath,
    uploadFolderId,
    assetsUploadProgress,
    uploadState,
    erroredAssets,
    keepExistingFolders,
    assetsLoading,
    isAllSelected,
    filesData,
  } = session;

  useEffect(() => {
    if (isAllSelected && filesByFolder) {
      const allAssets = Object.values(filesByFolder)
        .flat()
        .map((asset) => ({
          id: asset.id,
          name: asset.name,
          originalName: asset.originalName,
          type: DAMNodeType.Asset,
        }));

      // The select-all controls already write the full selection, so skip the redundant
      // second write (and the re-render of the whole list it would cost) when it is in sync.
      if (useUploadStore.getState().uploads[uploadSessionId]?.selectedAssets.length === allAssets.length) {
        return;
      }

      setUploadField(uploadSessionId, 'selectedAssets', allAssets);
    }
  }, [isAllSelected, filesByFolder, setUploadField, uploadSessionId]);

  const settings = useTableStore((s) => s.settings[TABLE_ENTITY] ?? defaultColumnsSettings[TABLE_ENTITY]);
  const setSettings = useTableStore((s) => s.setSettings);
  const findNodePath = useTreeStore((state) => state.findNodePath);
  const rootFolders = useTreeStore((state) => state.rootFolders);
  const reusableTreeSelectedFolder = useTreeStore((state) => state.reusableTreeSelectedFolder);

  useEffect(() => {
    // only write defaults once on first render
    if (!useTableStore.getState().settings[TABLE_ENTITY]) {
      setSettings(TABLE_ENTITY, defaultColumnsSettings[TABLE_ENTITY]);
    }
  }, [setSettings]);

  const uploadFolderSegments = useMemo(() => {
    const activeFolderId = reusableTreeSelectedFolder ?? uploadFolderId;
    if (!activeFolderId) {
      return undefined;
    }

    const path = findNodePath(activeFolderId, 'name');
    return path?.length ? path : undefined;
    // findNodePath's identity is stable; rootFolders forces the recompute when the tree changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reusableTreeSelectedFolder, uploadFolderId, findNodePath, rootFolders]);

  const getGroupedPathSegments = useMemo(() => {
    if (!uploadFolderSegments?.length || !uploadFolderPath) {
      return undefined;
    }

    const basePrefix = uploadFolderPath.endsWith('/') ? uploadFolderPath : `${uploadFolderPath}/`;

    return (path: string) => {
      if (path === uploadFolderPath) {
        return uploadFolderSegments;
      }

      if (!path.startsWith(basePrefix)) {
        return undefined;
      }

      const relative = path.slice(basePrefix.length);
      if (!relative) {
        return uploadFolderSegments;
      }

      const relativeSegments = relative.split('/').filter(Boolean);
      return [...uploadFolderSegments, ...relativeSegments];
    };
  }, [uploadFolderSegments, uploadFolderPath]);

  // Column configs are only rebuilt on layout-affecting changes; a ref keeps this click handler
  // reading the latest selection state without forcing that rebuild on every selection change.
  const handleSelectionRef = useRef<(row: Row<Asset>, event?: SelectionEventProps) => void>(() => {});

  const columnConfigs: Array<ColumnConfig<UploadAsset>> = useMemo(
    () => [
      {
        type: ColumnType.Group,
        opts: { accessorKey: 'path', header: 'Folder' },
      },
      {
        type: ColumnType.Select,
        opts: {
          size: 40,
          minSize: 40,
          maxSize: 40,
          galleryView,
          onClick: (e: MouseEvent<HTMLElement>, row: Row<Asset>) => {
            e.stopPropagation();
            // force it to behave like command+click
            handleSelectionRef.current(row, Object.assign(e, { metaKey: true }));
          },
        },
      },
      {
        type: ColumnType.Thumbnail,
        opts: {
          galleryView,
          thumbnailSize,
          variantKey: Variants.ThumbnailInternal,
          getDisablePolling: () => true, // We don't want to poll for scan status or thumbnail updates in the ingestion
          imageContainerClassNameFn: (row: Asset) =>
            cn({
              'bg-status-failure/50': erroredAssets[row.id],
            }),
          imageClassNameFn: (row: Asset) =>
            cn({
              'mb-1': galleryView === GalleryView.TILE,
              'opacity-50':
                erroredAssets[row.id] ||
                (uploadState === TaskState.InProgress &&
                  (!assetsUploadProgress[row.id]?.progress ||
                    assetsUploadProgress[row.id].progress !== UploadFinishedPercentage)),
            }),
          iconClassNameFn: (row: Asset) =>
            cn({
              'stroke-status-failure/20': erroredAssets[row.id],
            }),
          overlayIconClassNameFn: (row: Asset) => cn({ 'stroke-text-base': erroredAssets[row.id] }),
          getType: (row: Row<Asset>) => {
            const mime = row.original.type as string;
            return mime.includes('/') ? mime : 'RAW';
          },
        },
      },
      {
        type: ColumnType.Text,
        opts: {
          id: 'name',
          accessorKey: 'name',
          accessorFn: (row: Asset) => row.name,
          label: 'Name',
          galleryView,
          isMainColumn: true,
        },
      },
      {
        type: ColumnType.Type,
        opts: {
          accessorKey: 'format',
          label: 'Type',
          galleryView,
          cellClassName: 'uppercase',
          badgeClass: 'uppercase!',
          accessorFn: (row: Asset) => {
            const mime = row.type as string;
            return mime.includes('/') ? mime.split('/')[1] : 'RAW';
          },
          fileNameFn: (row: Asset) => row.originalName,
        },
      },
      {
        type: ColumnType.Dimensions,
        opts: {
          galleryView,
        },
      },
      {
        type: ColumnType.Text,
        opts: {
          accessorKey: 'fileName',
          accessorFn: (row: Asset) => row.originalName,
          label: 'Original File Name',
          galleryView,
          truncateHeaderValue: true,
        },
      },
      {
        type: ColumnType.Size,
        opts: {
          accessorKey: 'size',
          label: 'Size',
          galleryView,
        },
      },
      {
        type: ColumnType.Progress,
        opts: {
          galleryView,
          getProgress: (row: Asset) => assetsUploadProgress[row.id]?.progress,
          getError: (row: Asset) => Boolean(erroredAssets[row.id]),
          isUploading: (row: Asset) =>
            uploadState !== TaskState.NotStarted &&
            !erroredAssets[row.id] &&
            (!assetsUploadProgress[row.id]?.progress ||
              assetsUploadProgress[row.id].progress !== UploadFinishedPercentage),
        },
      },
    ],
    [galleryView, thumbnailSize, erroredAssets, uploadState, assetsUploadProgress],
  );

  const tableColumns: Array<ColumnDef<UploadAsset>> = useMemo(() => buildColumns(columnConfigs), [columnConfigs]);

  // Sync table rowSelection with the selectedAssets
  const rowSelection = useMemo(() => {
    const selection: Record<string, boolean> = {};

    selectedAssets.forEach(({ id }) => {
      selection[id] = true;
    });

    return selection;
  }, [selectedAssets]);

  const isDevelopment = process.env.NODE_ENV === 'development';

  const naturalCollator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }), []);
  const normalizePath = (p = '') => p.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');

  const tableData = useMemo(() => {
    const rows = Object.entries(filesByFolder).flatMap(([folderPath, files]) =>
      files.map((file) => ({
        ...file,
        path: keepExistingFolders ? folderPath : uploadFolderPath,
        folderPath: keepExistingFolders ? file.path : uploadFolderPath,
      })),
    );

    // Sort folder path asc first
    return rows
      .map((row, i) => ({ row, i }))
      .sort((a, b) => {
        const byFolder = naturalCollator.compare(normalizePath(a.row.path), normalizePath(b.row.path));

        if (byFolder !== 0) {
          return byFolder;
        }

        // Then file name asc
        const byFile = naturalCollator.compare(a.row.name ?? '', b.row.name ?? '');

        if (byFile !== 0) {
          return byFile;
        }

        return a.i - b.i;
      })
      .map(({ row }) => row);
  }, [filesByFolder, keepExistingFolders, uploadFolderPath, naturalCollator]);

  const selectedAssetIds = useMemo(() => new Set(selectedAssets.map(({ id }) => id)), [selectedAssets]);

  // Totals for every folder bar plus the global bar in a single pass over the ingestion, so a
  // folder upload with tens of thousands of assets costs one O(n) sweep per selection change
  // rather than a per-row lookup (and never a per-asset state write).
  const selectionStats = useMemo(() => {
    const byFolder = new Map<string, { total: number; selected: number }>();
    let selected = 0;

    for (const asset of tableData) {
      const folder = byFolder.get(asset.path) ?? { total: 0, selected: 0 };
      folder.total += 1;

      if (selectedAssetIds.has(asset.id)) {
        folder.selected += 1;
        selected += 1;
      }

      byFolder.set(asset.path, folder);
    }

    return { byFolder, selected, total: tableData.length };
  }, [tableData, selectedAssetIds]);

  const isEverythingSelected = selectionStats.total > 0 && selectionStats.selected === selectionStats.total;

  const applySelection = (nextSelection: UploadSession['selectedAssets'], nextIsAllSelected: boolean) => {
    // `isAllSelected` has to be written before the selection: while it is true an effect above
    // re-materialises every asset, which would undo a partial selection.
    setUploadField(uploadSessionId, 'isAllSelected', nextIsAllSelected);
    setUploadField(uploadSessionId, 'selectedAssets', nextSelection);
    setUploadField(
      uploadSessionId,
      'currentSelectedType',
      nextSelection.length > 0 ? NodeType.Assets : NodeType.Folders,
    );
  };

  const toSelectedAsset = ({ id, name, originalName }: UploadAsset) => ({
    id,
    name,
    originalName,
    type: DAMNodeType.Asset,
  });

  const toggleSelectAll = () =>
    applySelection(isEverythingSelected ? [] : tableData.map(toSelectedAsset), !isEverythingSelected);

  const toggleFolderSelection = (folderPath: string) => {
    const folderStats = selectionStats.byFolder.get(folderPath);

    if (!folderStats) {
      return;
    }

    const folderRows = tableData.filter((row) => row.path === folderPath);
    const folderRowIds = new Set(folderRows.map(({ id }) => id));
    const withoutFolder = selectedAssets.filter(({ id }) => !folderRowIds.has(id));

    // A partly selected folder fills up first; only a full one clears.
    const nextSelection =
      folderStats.selected === folderStats.total
        ? withoutFolder
        : [...withoutFolder, ...folderRows.map(toSelectedAsset)];

    applySelection(nextSelection, nextSelection.length === selectionStats.total);
  };

  // Disable dropzone if uploadFolderPath is empty
  const isDropzoneDisabled = !uploadFolderPath || uploadState !== TaskState.NotStarted;

  // No `accept`: listing many mime types/extensions in the native file-picker crashes Chrome at certain screen resolutions.
  const { getRootProps, getInputProps, isFocused, isDragAccept, open } = useDropzone({
    multiple: true,
    disabled: isDropzoneDisabled,
    maxFiles: undefined,
    noClick: tableData.length > 0,
    noKeyboard: tableData.length > 0,
    onDrop: (droppedFiles) => {
      setIsFolderPickerActive(false);

      if (droppedFiles.length > 0) {
        void onDrop(droppedFiles, uploadSessionId);
      }
    },
    onFileDialogCancel: () => {
      setIsFolderPickerActive(false);
    },
  });

  const handleAddFolderClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (isDropzoneDisabled) {
      return;
    }

    if (disableFolderPicker) {
      open();
      return;
    }

    flushSync(() => {
      setIsFolderPickerActive(true);
    });

    open();
  };

  const table = useReactTable<UploadAsset>({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: false,
    state: {
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      grouping: grouping,
      rowSelection: rowSelection,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onColumnVisibilityChange: (updater: Updater<VisibilityState>) =>
      setSettings(TABLE_ENTITY, {
        columnVisibility: typeof updater === 'function' ? updater(settings.columnVisibility) : updater,
      }),
    onColumnOrderChange: (updater: Updater<ColumnOrderState>) =>
      setSettings(TABLE_ENTITY, {
        columnOrder: typeof updater === 'function' ? updater(settings.columnOrder) : updater,
      }),

    getRowId: (row) => row.id,
    getGroupedRowModel: getGroupedRowModel(),
    debugTable: isDevelopment,
    debugHeaders: isDevelopment,
    debugColumns: isDevelopment,
    manualPagination: true,
    getPaginationRowModel: getPaginationRowModel(),
  });

  const dataIds = useMemo<Array<UniqueIdentifier>>(() => tableData.map(({ id }) => id) || [], [tableData]);

  const handleSelection = <TData extends Asset>(row: Row<TData>, event?: SelectionEventProps) => {
    if (!event) {
      return;
    }
    const { metaKey, shiftKey, ctrlKey } = event;

    // Disable de-selecting an asset by clicking on it directly
    if (selectedAssets.length === 1 && selectedAssets[0].id === row.original.id && !metaKey && !shiftKey && !ctrlKey) {
      return;
    }

    if (isAllSelected) {
      setUploadField(uploadSessionId, 'isAllSelected', false);
    }

    const { id, name, originalName } = row.original;
    const sessionSelectedAssetIds = useUploadStore.getState().uploads[uploadSessionId].selectedAssets;
    const isSelected = sessionSelectedAssetIds.some((selected) => selected.id === id);

    if (shiftKey) {
      const currentIndex = tableData.findIndex((item) => item.id === id);
      const lastSelectedId = sessionSelectedAssetIds.at(-1)?.id ?? null;
      const lastSelectedIndex = lastSelectedId ? tableData.findIndex((item) => item.id === lastSelectedId) : -1;

      if (lastSelectedIndex >= 0) {
        const start = Math.min(currentIndex, lastSelectedIndex);
        const end = Math.max(currentIndex, lastSelectedIndex);

        const rangeSelections = tableData.slice(start, end + 1).map((item) => ({
          id: item.id,
          name: item.name,
          originalName: item.originalName,
          type: item.type,
        }));

        const existingIds = new Set(sessionSelectedAssetIds.map((item) => item.id));
        const uniqueRangeSelections = rangeSelections.filter((item) => !existingIds.has(item.id));

        setUploadField(uploadSessionId, 'selectedAssets', [...sessionSelectedAssetIds, ...uniqueRangeSelections]);
      } else {
        setUploadField(uploadSessionId, 'selectedAssets', [
          ...sessionSelectedAssetIds,
          { id, name, originalName, type: DAMNodeType.Asset },
        ]);
      }
    } else if (metaKey || ctrlKey) {
      const updated = isSelected
        ? sessionSelectedAssetIds.filter((selected) => selected.id !== id)
        : [...sessionSelectedAssetIds, { id, name, originalName, type: DAMNodeType.Asset }];
      setUploadField(uploadSessionId, 'selectedAssets', updated);
    } else {
      if (isSelected && sessionSelectedAssetIds.length === 1) {
        setUploadField(uploadSessionId, 'selectedAssets', []);
      } else {
        setUploadField(uploadSessionId, 'selectedAssets', [{ id, name, originalName, type: DAMNodeType.Asset }]);
      }
    }

    const newSelected = getUploadSession(uploadSessionId)?.selectedAssets ?? [];
    if (newSelected.length === 0) {
      setUploadField(uploadSessionId, 'currentSelectedType', NodeType.Folders);
    } else {
      setUploadField(uploadSessionId, 'currentSelectedType', NodeType.Assets);
    }
  };
  handleSelectionRef.current = handleSelection;

  const handleRightClick = (asset: Asset) => {
    contextNodeSelection({ id: asset.nodeId, name: asset.name, type: asset.type });
    useUploadStore.getState().setUploadField(uploadSessionId, 'currentSelectedType', NodeType.Assets);
    // contextAssetSelection({ id: asset.id, name: asset.name, type: asset.type });
  };

  const { breadcrumbs, lastItem } = useMemo(
    () => transformPath(uploadFolderSegments ?? uploadFolderPath),
    [uploadFolderSegments, uploadFolderPath],
  );

  const renderGroupHeader = (row: Row<UploadAsset>) => {
    const path = (row.groupingValue as string) ?? row.getValue<string>('path') ?? '';
    const normalizedPathSegments = getGroupedPathSegments ? getGroupedPathSegments(path) : undefined;
    const { breadcrumbs: groupingBreadcrumbs, lastItem: groupingLastItem } = transformPath(
      normalizedPathSegments ?? path,
    );

    const folderStats = selectionStats.byFolder.get(path) ?? { total: 0, selected: 0 };
    const folderName = groupingLastItem?.name ?? path;
    const isFolderSelected = folderStats.total > 0 && folderStats.selected === folderStats.total;

    return (
      <>
        {/* `flex-1 min-w-0` gives this a flex basis of 0, so its width is exactly the space the
            `shrink-0` action group leaves over and never depends on how long the path is. That is
            what the breadcrumb measures, so it collapses into the "..." menu at the right moment
            instead of overflowing and being clipped - which is what a hard-coded reserved width
            got wrong every time the action group changed size. */}
        <div className="flex min-w-0 flex-1 items-center">
          <Breadcrumbs items={groupingBreadcrumbs} lastItem={groupingLastItem} rootIcon={FolderInput} readOnly />
        </div>
        <div className="flex shrink-0 items-center">
          <SelectAllCheckbox
            label={isFolderSelected ? tUpload('deselectAllInFolder') : tUpload('selectAllInFolder')}
            alternateLabel={isFolderSelected ? tUpload('selectAllInFolder') : tUpload('deselectAllInFolder')}
            accessibleLabel={tUpload(isFolderSelected ? 'deselectAllInFolderLabel' : 'selectAllInFolderLabel', {
              count: folderStats.total,
              folderName,
            })}
            checked={isFolderSelected}
            indeterminate={folderStats.selected > 0 && !isFolderSelected}
            onToggle={() => toggleFolderSelection(path)}
            dataCy="ingestion-folder-select-all-checkbox"
            className="mx-3"
            shape="folder"
          />
          <Separator orientation="vertical" className="h-6 bg-ui-light" />
          {/* DM-2522 third ask: the bare X was ambiguous. The visible label says what it does; the
              accessible name repeats it verbatim and adds the folder so screen-reader users can
              tell one folder bar's remove control from another's. */}
          <Button
            variant="main-toolbar"
            size="sm"
            className="font-bold text-text-gray!"
            iconClassName="icon-5"
            disabled={uploadState !== TaskState.NotStarted}
            aria-label={tUpload('removeFolderLabel', { folderName })}
            data-cy="ingestion-remove-folder-button"
            onClick={() => removeFolder(uploadSessionId, row.renderValue('path'))}
            icon={X}
          >
            {tUpload('removeFolder')}
          </Button>
        </div>
      </>
    );
  };

  const breadCrumbActions = () => (
    <>
      <AddAssetsToUploadMenuItem onClick={open} disabled={uploadState !== TaskState.NotStarted} />
      <ClearUploadMenuItem disabled={tableData.length === 0} keepExistingFoldersOnClear={keepExistingFoldersOnClear} />
    </>
  );

  return (
    <div className="flex h-full flex-col space-y-px">
      {!uploadFolderPath ? (
        <div className="flex h-12 max-h-12 min-h-12 items-stretch justify-between divide-x divide-ui-void-gap bg-status-warning text-text-inverted">
          <div className="ml-3 flex flex-1 items-center gap-x-3">
            <FolderInput className="min-h-6 min-w-6 stroke-text-inverted dark:stroke-text-inverted" strokeWidth={2} />
            <div className="text-base font-bold text-text-inverted dark:text-text-inverted">
              {tUpload('folderNotSelected')}
            </div>
          </div>
          <Button
            variant="warning"
            className="h-full justify-center text-sm font-bold text-text-base! dark:text-text-inverted!"
            data-cy="open-upload-dialog-button"
            onClick={() => openModal(ModalId.IngestionFolderSelect)}
          >
            {tUpload('selectFolder')}
          </Button>
        </div>
      ) : (
        <Toolbar
          ref={toolbarRef}
          isUpload={!usePageInspector}
          className="justify-between border-t border-t-toolbar-bevel pl-1"
        >
          {/* Same basis-0 wrapper as the folder bars: the toolbar controls keep their width and the
              breadcrumb gets exactly what is left, so shrinking the pane with the inspector
              collapses the path rather than clipping it. */}
          <div className="flex min-w-0 flex-1 items-center">
            {!hideBreadcrumb && (
              <Breadcrumbs lastItem={lastItem} items={breadcrumbs} actionMenu={breadCrumbActions} readOnly />
            )}
          </div>

          <div className="flex shrink-0 items-center">
            <Separator orientation="vertical" className="h-6 bg-ui-light" />
            {/* Leftmost item of the right-hand group, and deliberately outside the `collapsed`
                branch below: DM-2522 is about select-all being hard to reach, so it must never
                fold into the "more options" menu. Sort/View/Settings keep their own spacing. */}
            {selectionStats.total > 0 && (
              <>
                <SelectAllCheckbox
                  label={isEverythingSelected ? tUpload('deselectAll') : tUpload('selectAll')}
                  alternateLabel={isEverythingSelected ? tUpload('selectAll') : tUpload('deselectAll')}
                  accessibleLabel={tUpload(isEverythingSelected ? 'deselectAllLabel' : 'selectAllLabel', {
                    count: selectionStats.total,
                  })}
                  checked={isEverythingSelected}
                  indeterminate={selectionStats.selected > 0 && !isEverythingSelected}
                  onToggle={toggleSelectAll}
                  dataCy="ingestion-select-all-checkbox"
                  className="mx-3"
                />
                <Separator orientation="vertical" className="h-6 bg-ui-light" />
              </>
            )}
            {collapsed ? (
              <DropdownMenu>
                <DropdownMenuTriggerButton
                  variant="main-toolbar"
                  size="main-toolbar"
                  icon={Menu}
                  data-cy="toolbar-dropdown-menu-button"
                  tooltip={tCommon('moreOptions')}
                  className="mx-1"
                />
                <DropdownMenuContent>
                  <MenuProvider primitives={dropdownPrimitives}>
                    <MenuSub label={tCommon('view')}>
                      <ToggleViewMenu />
                      <MenuSeparator />
                      <ColumnsVisibilitySubmenu table={table} />
                      <MenuSeparator />
                      <ZoomInMenuItem />
                      <ZoomOutMenuItem />
                    </MenuSub>
                  </MenuProvider>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex flex-row items-center">
                <DropdownSelect title={tCommon('view')}>
                  <ToggleViewMenu />
                  <MenuSeparator />
                  <ColumnsVisibilitySubmenu table={table} />
                  <MenuSeparator />
                  <ZoomInMenuItem />
                  <ZoomOutMenuItem />
                </DropdownSelect>
              </div>
            )}
            <Separator orientation="vertical" className="h-6 bg-ui-light" />
          </div>
        </Toolbar>
      )}
      <UploadsTreeSelectorDialog />
      {onError ? (
        <EmptyLayout icon={FileX} title={tCommon('oopsSomethingWentWrong')} message={tCommon('pleaseTryAgainLater')} />
      ) : (
        <div
          id="upload-dropzone"
          className={cn(
            'dropzone relative flex grow flex-col overflow-x-auto',
            tableData.length ? '' : 'items-center justify-center',
            isDragAccept && !tableData.length && 'bg-green-500/5',
            isDropzoneDisabled && 'cursor-not-allowed select-none',
          )}
          {...getRootProps()}
        >
          {tableData.length || assetsLoading ? (
            <>
              <DataTableGridListView
                galleryView={galleryView}
                table={table}
                data={tableData as Array<UploadAsset>}
                dataIds={dataIds}
                selectedAssets={selectedAssets}
                handleClick={handleSelection}
                handleRightClick={handleRightClick}
                isAssetsLoading={assetsLoading}
                isLoading={false}
                isUpload
                slots={{
                  renderListGroupRow: ({ row }) => (
                    <tr className="h-12 p-0">
                      <td className="p-0" colSpan={table.getVisibleLeafColumns().length}>
                        <div className="flex min-h-12 items-center justify-between gap-x-3 border-b border-b-ui-void-gap bg-ui-panel pr-3.5 pl-1 text-base">
                          {renderGroupHeader(row)}
                        </div>
                      </td>
                    </tr>
                  ),
                  renderGridGroupHeader: ({ row }) => (
                    <div className="flex min-h-12 items-center justify-between gap-x-3 border-y border-y-ui-void-gap bg-ui-panel pr-3.5 pl-1 text-base first:border-t-0">
                      {renderGroupHeader(row)}
                    </div>
                  ),
                }}
              />
              {selectedAssets.length > 0 && (
                <SelectionBar
                  rowCount={filesData.length ?? 0}
                  nodeName={'upload'}
                  selectedAssets={selectedAssets as Array<Asset>}
                  setSelectedAssets={(ids: Array<Asset>) => {
                    setUploadField(uploadSessionId, 'selectedAssets', ids);
                  }}
                  isUpload
                />
              )}
            </>
          ) : (
            <div className="flex size-full flex-col items-center justify-center gap-y-2 bg-ui-container p-4">
              <input
                {...getInputProps({
                  dir: isFolderPickerActive ? 'true' : undefined,
                  webkitdirectory: isFolderPickerActive ? 'true' : undefined,
                })}
              />
              <ImagePlus
                className={cn(
                  'mx-auto min-h-20 text-icon-base icon-64',
                  isFocused && 'text-neutral-500',
                  isDragAccept && 'text-green-500',
                )}
                strokeWidth={0.5}
              />

              <label htmlFor="file-upload" className="relative cursor-pointer">
                {uploadFolderPath ? (
                  <div className="flex flex-col gap-y-1">
                    <h4 className="text-center text-2xl text-balance text-text-base">
                      {emptyStateCopy?.title ?? tUpload('uploadAssetsTitle')}
                    </h4>
                    <div className="text-center text-sm font-normal text-balance text-text-base">
                      {emptyStateCopy?.description ??
                        tUpload.rich('uploadFiles', {
                          highlight: (chunks) => <span className="font-bold">{chunks}</span>,
                        })}
                    </div>
                    <Button
                      variant="navigation-action-primary"
                      onClick={handleAddFolderClick}
                      className="mt-2 self-center px-6 py-3 focus-visible:ring-offset-ui-container"
                      data-cy="ingestion-add-folder-button"
                      icon={UploadIcon}
                      iconClassName="icon-6"
                    >
                      {emptyStateCopy?.buttonLabel ?? tUpload('selectFolderToUpload')}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center text-sm font-normal text-balance text-text-base">
                    {tUpload('selectDestinationFolder')}
                  </div>
                )}
              </label>
            </div>
          )}
        </div>
      )}
      <CsvDialog />
    </div>
  );
};
